import { Check, Home, Sparkles, Bed } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';

const wetAreaPricing = [
  { service: 'Quick Light Clean (1 hour)', price: 'from $50', affordable: true },
  { service: 'Bathroom Clean', price: 'from $75' },
  { service: 'Kitchen Clean', price: 'from $95' },
  { service: 'Laundry Clean', price: 'from $55' },
  { service: 'Vacate Wet Area Package', price: 'from $200', featured: true }
];

const airbnbPricing = [
  { size: '1 Bedroom / 1 Bathroom', price: 'from $100' },
  { size: '2 Bedroom / 1–2 Bathroom', price: 'from $150' },
  { size: '3 Bedroom / 2 Bathroom', price: 'from $210', featured: true },
  { size: '4 Bedroom / 2–3 Bathroom', price: 'from $300' }
];

const airbnbAdditional = [
  { name: 'Linen Change', price: 'from $30' },
  { name: 'Restocking Supplies', price: 'from $20' },
  { name: 'Same Day Turnaround', price: 'from $30' }
];

const fullHousePricing = [
  { size: '1 Bedroom / 1 Bathroom', price: 'from $100' },
  { size: '2 Bedroom / 1–2 Bathroom', price: 'from $140' },
  { size: '3 Bedroom / 2 Bathroom', price: 'from $180', featured: true },
  { size: '4 Bedroom / 2–3 Bathroom', price: 'from $260' },
  { size: '5 Bedroom / 2–3 Bathroom', price: 'from $330+' }
];

const additionalServices = [
  { name: 'Deep Clean Upgrade', price: 'from $80' },
  { name: 'Extra Wet Area', price: 'from $40' },
  { name: 'Heavy Mould Treatment', price: 'from $50' },
  { name: 'Deep Oven Clean', price: 'from $80' },
  { name: 'Heavy Kitchen Degrease', price: 'from $60' }
];

export const ComprehensivePricing = () => {
  return (
    <section id="pricing" className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Simple, Honest Pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Transparent pricing with no hidden fees. Quality cleaning at fair rates.
          </p>
        </div>

        {/* Wet Area Pricing */}
        <div className="mb-16">
          <Card className="border-2 border-gray-200">
            <CardHeader className="bg-gradient-to-r from-teal-50 to-blue-50">
              <CardTitle className="text-2xl flex items-center gap-2">
                <Sparkles className="text-teal-600" size={24} />
                Wet Area Specialist Services
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {wetAreaPricing.map((item, index) => (
                  <div 
                    key={index}
                    className={`flex justify-between items-center p-4 rounded-lg ${
                      item.featured 
                        ? 'bg-teal-50 border-2 border-teal-600' 
                        : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Check className="text-teal-600 flex-shrink-0" size={20} />
                      <span className={`font-medium ${item.featured ? 'text-teal-900' : 'text-gray-700'}`}>
                        {item.service}
                      </span>
                    </div>
                    <span className="font-bold text-teal-600">{item.price}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-600 mt-4">* Final price may vary depending on size and condition.</p>
            </CardContent>
          </Card>
        </div>

        {/* Airbnb & Short Stay Pricing */}
        <div className="mb-16">
          <Card className="border-2 border-teal-500 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-teal-100 to-blue-100">
              <CardTitle className="text-2xl flex items-center gap-2">
                <Bed className="text-teal-600" size={24} />
                Airbnb & Short Stay Cleaning
              </CardTitle>
              <CardDescription className="text-base mt-2">
                We provide reliable and professional cleaning for Airbnb and short stay properties, ensuring your space is always clean, fresh and welcoming for every guest.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-bold text-gray-900 mb-4">Property Cleaning</h3>
                  <div className="space-y-3">
                    {airbnbPricing.map((item, index) => (
                      <div 
                        key={index}
                        className={`flex justify-between items-center p-4 rounded-lg ${
                          item.featured 
                            ? 'bg-teal-50 border-2 border-teal-600' 
                            : 'bg-gray-50'
                        }`}
                      >
                        <span className={`font-medium text-sm ${item.featured ? 'text-teal-900' : 'text-gray-700'}`}>
                          {item.size}
                        </span>
                        <span className="font-bold text-teal-600">{item.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="font-bold text-gray-900 mb-4">Additional Services</h3>
                  <div className="space-y-3">
                    {airbnbAdditional.map((item, index) => (
                      <div 
                        key={index}
                        className="flex justify-between items-center p-4 rounded-lg bg-gray-50"
                      >
                        <span className="font-medium text-sm text-gray-700">{item.name}</span>
                        <span className="font-bold text-teal-600">{item.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-6">
                * Final pricing depends on property size, condition and turnaround requirements. Our service includes cleaning, resetting the space and preparing the property for the next booking.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Full House Cleaning */}
        <div className="mb-16">
          <Card className="border-2 border-gray-200">
            <CardHeader className="bg-gradient-to-r from-teal-50 to-blue-50">
              <CardTitle className="text-2xl flex items-center gap-2">
                <Home className="text-teal-600" size={24} />
                Full House Cleaning
              </CardTitle>
              <CardDescription className="text-base mt-2">
                In addition to our wet area specialist services, we also offer full house cleaning to help keep your entire home fresh and tidy. This includes vacuuming, mopping, general surface cleaning and attention to high-use areas.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fullHousePricing.map((item, index) => (
                  <div 
                    key={index}
                    className={`flex justify-between items-center p-4 rounded-lg ${
                      item.featured 
                        ? 'bg-teal-50 border-2 border-teal-600' 
                        : 'bg-gray-50'
                    }`}
                  >
                    <span className={`font-medium ${item.featured ? 'text-teal-900' : 'text-gray-700'}`}>
                      {item.size}
                    </span>
                    <span className="font-bold text-teal-600">{item.price}</span>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded mt-6">
                <p className="text-sm text-gray-700">
                  <strong>Please note:</strong> Deep cleaning, heavy build-up, ovens and detailed mould treatment are not included and can be added as an additional service if required.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Additional Services */}
        <Card className="border-2 border-gray-200 mb-16">
          <CardHeader>
            <CardTitle className="text-2xl">Additional Services</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {additionalServices.map((service, index) => (
                <div 
                  key={index}
                  className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <span className="text-gray-700 font-medium">{service.name}</span>
                  <span className="text-teal-600 font-bold">{service.price}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={() => {
              const element = document.getElementById('booking');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-teal-600 hover:bg-teal-700 text-white px-10 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:shadow-lg"
          >
            Get Your Free Quote
          </button>
          <p className="text-gray-600 mt-4">No obligation • Fast response • Transparent pricing</p>
        </div>
      </div>
    </section>
  );
};
